import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Clock, MapPin, Phone, Mail, AlertCircle, Save } from 'lucide-react';
import { format } from 'date-fns';
import { Booking, BookingStatus } from '../../types';
import { updateOrder } from '../../utils/orders';

const timeSlots = [
  '9:00 AM - 11:00 AM',
  '11:00 AM - 1:00 PM',
  '1:00 PM - 3:00 PM',
  '3:00 PM - 5:00 PM'
];

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  order: Booking;
}

export function OrderDetailsModal({ isOpen, onClose, onSaved, order }: OrderDetailsModalProps) {
  const [newStatus, setNewStatus] = useState<BookingStatus>(order.status);
  const [installDate, setInstallDate] = useState(order.delivery_schedule?.date || '');
  const [installTime, setInstallTime] = useState(order.delivery_schedule?.time || '');
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate date and time if status is "Confirmed Install Date"
      if (newStatus === 'Confirmed Install Date') {
        if (!installDate) {
          setError('Please select an installation date');
          return;
        }
        if (!installTime) {
          setError('Please select an installation time');
          return;
        }
      }

      const updatedOrder = {
        ...order,
        status: newStatus,
        delivery_schedule: newStatus === 'Confirmed Install Date' ? {
          date: installDate,
          time: installTime
        } : order.delivery_schedule,
        status_history: [
          ...(order.status_history || []),
          {
            status: newStatus,
            timestamp: new Date().toISOString(),
            note: note.trim() || `Status updated to ${newStatus}`
          }
        ]
      };

      const { success, error } = await updateOrder(updatedOrder);

      if (!success) {
        throw new Error(error || 'Failed to update order');
      }

      onClose();
      onSaved?.();
    } catch (err) {
      console.error('Error updating order:', err);
      setError('Failed to update order. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date attribute
  const today = new Date().toISOString().split('T')[0];

  if (!order) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-xl font-semibold text-gray-900">
                Order #{order.order_number}
              </Dialog.Title>
              <Dialog.Close className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </Dialog.Close>
            </div>

            <div className="space-y-6">
              {/* Status Update Section */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-4">Update Status</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      Status
                    </label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value as BookingStatus)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Confirmed Install Date">Confirmed Install Date</option>
                      <option value="Done">Done</option>
                      <option value="Return/Refund">Return/Refund</option>
                      <option value="Voided">Voided</option>
                    </select>
                  </div>

                  {newStatus === 'Confirmed Install Date' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-1">
                          Installation Date
                        </label>
                        <input
                          type="date"
                          value={installDate}
                          min={today}
                          onChange={(e) => setInstallDate(e.target.value)}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-1">
                          Installation Time
                        </label>
                        <select
                          value={installTime}
                          onChange={(e) => setInstallTime(e.target.value)}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select time slot...</option>
                          {timeSlots.map((slot) => (
                            <option key={slot} value={slot}>{slot}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      Add Note
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      rows={3}
                      placeholder="Add a note about this status update..."
                    />
                  </div>

                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {error}
                    </div>
                  )}

                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>

              {/* Customer Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Customer Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">
                      {order.customer_email || 'No email provided'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">{order.customer_phone}</span>
                  </div>
                </div>
              </div>

              {/* Installation Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Installation Details
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="ml-2">
                      <p className="text-sm font-medium text-gray-900">Installation Address</p>
                      <p className="text-sm text-gray-600">
                        {order.delivery_address.street}<br />
                        {order.delivery_address.city}, {order.delivery_address.state} {order.delivery_address.zipCode}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Clock className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="ml-2">
                      <p className="text-sm font-medium text-gray-900">Installation Location</p>
                      <p className="text-sm text-gray-600">{order.installation_location}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status History */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-4">Status History</h3>
                <div className="space-y-4">
                  {order.status_history.map((update, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-900">{update.status}</p>
                        <p className="text-xs text-gray-500">
                          {format(new Date(update.timestamp), 'MMM d, yyyy h:mm a')}
                        </p>
                        {update.note && (
                          <p className="mt-1 text-sm text-gray-600">{update.note}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}